import { useTranslation } from 'react-i18next';
import { Button, Container, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { handleSubmitError } from '@utils/forms';

import { usePasswordResetRequestMutation } from '@api/passwordReset.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useGRecaptcha from '@hooks/useGRecaptcha';

export default function PasswordResetRequest() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { getRecaptchaToken } = useGRecaptcha();
  const { t } = useTranslation();

  const [params, setParams] = useQueryParams();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '',
  };

  const form = useForm({
    initialValues,
  });

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const recaptchaToken = await getRecaptchaToken('PasswordResetRequest');

      await passwordResetRequest({ body: values, recaptchaToken }).unwrap();

      params.set('p', 'login');
      params.set('alert', t('auth.passwordResetRequest.successMessage'));
      setParams(params);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Api
  // ==========================================================================
  const [passwordResetRequest, { isLoading }] =
    usePasswordResetRequestMutation();

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <Text ta="center" fw="bold" size="1.5rem">
        {t('auth.passwordResetRequest.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('auth.passwordResetRequest.knowPassword')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'login');
            setParams(params);
          }}
        >
          {t('auth.passwordResetRequest.loginLink')}
        </Text>
      </Text>

      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Text ta="center" size="sm" mb="lg" mt="xl">
          {t('auth.passwordResetRequest.instructions')}
        </Text>
        <TextInput
          label={t('auth.passwordResetRequest.email.label')}
          placeholder={t('auth.passwordResetRequest.email.placeholder')}
          required
          {...form.getInputProps('email')}
        />

        {form.errors.general && (
          <Text ta="center" c="red" size="sm" mt="xl">
            {form.errors.general}
          </Text>
        )}
        <Button
          type="submit"
          mx="auto"
          mt="xl"
          style={{ display: 'block' }}
          loading={isLoading}
        >
          {t('auth.passwordResetRequest.submit')}
        </Button>
      </form>
    </Container>
  );
}
